// filepath: apps/web/src/features/admin/secrets/SecretReferencesTable.tsx
//
// FR-UI-ADM-10 AC-1 -- table rendering a list of SecretMeta rows.
//
// Each row shows the secret name, secret_ref_id, status, created_at,
// updated_at, version, and a "Delete" action button that opens the
// SecretImpactDialog (the impact dialog MUST run BEFORE the delete
// mutation per AC-3 + AC-15).
//
// INV-5 contract: the table NEVER renders a `secret_value` field.
// The row data flows from useSecrets() which excludes `secret_value`
// from the wire type entirely.

import React from "react";
import { useTranslation } from "@/i18n/useTranslation";
import { formatDate } from "@/i18n/format";
import type { SecretMeta } from "./types";

interface SecretReferencesTableProps {
  rows: SecretMeta[];
  onRequestDelete: (secret: SecretMeta) => void;
  isDeleting: boolean;
}

export function SecretReferencesTable({
  rows,
  onRequestDelete,
  isDeleting,
}: SecretReferencesTableProps): React.ReactElement {
  const { t, locale } = useTranslation();
  if (rows.length === 0) {
    return (
      <div
        className="rounded border border-border bg-background p-6 text-center text-sm text-muted-foreground"
        data-testid="secrets-empty-state"
      >
        {t("admin.secrets.empty_state")}
      </div>
    );
  }
  return (
    <div
      className="overflow-x-auto rounded border border-border bg-background"
      data-testid="secrets-table-wrapper"
    >
      <table className="w-full text-sm" data-testid="secrets-table">
        <thead>
          <tr className="border-b border-border bg-muted text-left text-xs uppercase tracking-wide text-muted-foreground">
            <th className="px-3 py-2">{t("admin.secrets.table.name")}</th>
            <th className="px-3 py-2">{t("admin.secrets.table.ref_id")}</th>
            <th className="px-3 py-2">{t("admin.secrets.table.status")}</th>
            <th className="px-3 py-2">{t("admin.secrets.table.created_at")}</th>
            <th className="px-3 py-2">{t("admin.secrets.table.updated_at")}</th>
            <th className="px-3 py-2">{t("admin.secrets.table.version")}</th>
            <th className="px-3 py-2 text-right">
              {t("admin.secrets.table.actions")}
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr
              key={row.id}
              className="border-b border-border last:border-0"
              data-testid="secret-row"
            >
              <td className="px-3 py-2 font-medium">{row.name}</td>
              <td className="px-3 py-2 font-mono text-xs">
                {row.secret_ref_id}
              </td>
              <td className="px-3 py-2">
                <span
                  data-status={row.status}
                  className="inline-flex items-center rounded bg-muted px-2 py-0.5 text-xs"
                >
                  {row.status}
                </span>
              </td>
              <td className="px-3 py-2 text-xs text-muted-foreground">
                {formatDate(new Date(row.created_at), locale)}
              </td>
              <td className="px-3 py-2 text-xs text-muted-foreground">
                {formatDate(new Date(row.updated_at), locale)}
              </td>
              <td className="px-3 py-2 text-xs">{row.version}</td>
              <td className="px-3 py-2 text-right">
                <button
                  type="button"
                  disabled={isDeleting}
                  onClick={() => onRequestDelete(row)}
                  className="rounded border border-red-300 px-2 py-1 text-xs font-medium text-red-700 hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-50"
                  data-testid="secret-row-delete"
                >
                  {t("common.delete")}
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
